/**
 * App input component
 */

// React
import React from 'react';

// Library
import { Controller, PathValue, Control, FieldErrors, FieldValues, Path } from 'react-hook-form';

// Material
import { TextFieldProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';

type AppInputProps<T extends FieldValues> = Omit<TextFieldProps, 'variant'> & {
  name: Path<T>;
  control: Control<T>;
  type?: string;
  errors?: FieldErrors<T>;
  isTextarea?: boolean;
  rows?: number;
  maxRows?: number;
};

const AppInput = <T extends FieldValues>({
  name,
  control,
  type,
  errors,
  isTextarea = false,
  rows = 4,
  maxRows = 8,
  ...rest
}: AppInputProps<T>): JSX.Element => {
  const formatValue = (value: PathValue<T, (string | undefined) & Path<T>>) => {
    if (!value) return '';
    return value;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...restField } }) => (
        <CustomTextField
          {...restField}
          inputRef={ref}
          value={formatValue(restField?.value)}
          fullWidth
          type={type}
          error={!!errors?.[name]}
          multiline={isTextarea}
          rows={isTextarea ? rows : undefined}
          maxRows={isTextarea ? maxRows : undefined}
          {...rest}
        />
      )}
    />
  );
};

export default AppInput;

const CustomTextField = styled(TextField)(({ theme, multiline }) => ({
  '& .MuiInputBase-root': {
    height: multiline ? 'auto' : '100%',
    width: '100%',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    color: '#666666',
    [theme.breakpoints.down('sm')]: {
      height: multiline ? 'auto' : '50px',
      padding: multiline ? '16.5px' : '0 16.5px',
    },
  },
  '& .MuiInputBase-inputMultiline': {
    padding: '0px',
  },
  '& textarea::-webkit-scrollbar': {
    width: '8px',
  },
  '& textarea::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '& textarea::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    '&:hover': {
      background: '#555',
    },
  },
}));
